import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { orgAPI, policiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Users, Building, Settings, Shield, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

export default function OrgAdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgDetails, setOrgDetails] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, detailsRes] = await Promise.all([
        orgAPI.getUsers(),
        orgAPI.getDetails()
      ]);
      setOrgUsers(usersRes.data);
      setOrgDetails(detailsRes.data);
      setOrgName(detailsRes.data.name || '');
      
      if (user?.org_id) {
        const policyRes = await policiesAPI.get(user.org_id);
        setPolicy(policyRes.data);
        try {
          const mfaRes = await orgAPI.getMfaSetting();
          setMfaEnabled(mfaRes.data.mfa_enabled);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [user?.org_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpdateOrg = async () => {
    try {
      await orgAPI.updateDetails({ name: orgName });
    } catch (e) {
      console.error(e);
    }
  };

  const updatePolicy = async (field, value) => {
    if (!user?.org_id) return;
    const updated = { ...policy, [field]: value };
    setPolicy(updated);
    try {
      await policiesAPI.update(user.org_id, updated);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-in" data-testid="org-admin-dashboard">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>
            Organization Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage users, policies, and settings for {orgDetails?.name || 'your organization'}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="users" data-testid="org-tab-users">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="details" data-testid="org-tab-details">
              <Building className="w-4 h-4 mr-2" />Organization
            </TabsTrigger>
            <TabsTrigger value="policy" data-testid="org-tab-policy">
              <Shield className="w-4 h-4 mr-2" />Policy
            </TabsTrigger>
          </TabsList>

          {/* USERS */}
          <TabsContent value="users">
            <div className="rounded-lg border bg-card">
              <div className="p-5 border-b">
                <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>Organization Users ({orgUsers.length})</h2>
              </div>
              <ScrollArea className="max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Agreement</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgUsers.map(u => (
                      <TableRow key={u.id} data-testid={`org-user-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{u.role}</Badge></TableCell>
                        <TableCell>
                          <Badge variant={u.agreement_accepted ? 'default' : 'outline'} className="text-xs">
                            {u.agreement_accepted ? 'Accepted' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {orgUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">No users found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* DETAILS */}
          <TabsContent value="details">
            <div className="max-w-lg rounded-lg border bg-card p-6 space-y-6">
              <h3 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>Organization Details</h3>
              <div className="space-y-2">
                <Label>Organization ID</Label>
                <Input value={orgDetails?.id || ''} disabled className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  data-testid="org-name-input"
                />
              </div>
              <Button onClick={handleUpdateOrg} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="update-org-button">
                Save Changes
              </Button>
            </div>
          </TabsContent>

          {/* POLICY */}
          <TabsContent value="policy">
            {policy ? (
              <div className="max-w-lg rounded-lg border bg-card p-6 space-y-6" data-testid="org-policy-editor">
                <h3 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>Organization Policy</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Allow Transcript</Label>
                      <p className="text-xs text-muted-foreground">Users can view session transcripts</p>
                    </div>
                    <Switch
                      checked={policy.transcript_allowed}
                      onCheckedChange={v => updatePolicy('transcript_allowed', v)}
                      data-testid="org-policy-transcript"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Domestic Only</Label>
                      <p className="text-xs text-muted-foreground">Restrict to US-based interpreters</p>
                    </div>
                    <Switch
                      checked={policy.domestic_only}
                      onCheckedChange={v => updatePolicy('domestic_only', v)}
                      data-testid="org-policy-domestic"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Require High Risk Acknowledgement</Label>
                      <p className="text-xs text-muted-foreground">Must acknowledge before continuing with risky translations</p>
                    </div>
                    <Switch
                      checked={policy.high_risk_ack_required}
                      onCheckedChange={v => updatePolicy('high_risk_ack_required', v)}
                      data-testid="org-policy-highrisk"
                    />
                  </div>
                  <div>
                    <Label className="font-medium mb-2 block">Domain Level</Label>
                    <Select value={policy.domain_level} onValueChange={v => updatePolicy('domain_level', v)}>
                      <SelectTrigger data-testid="org-policy-domain">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LITERAL">Literal</SelectItem>
                        <SelectItem value="BALANCED">Balanced</SelectItem>
                        <SelectItem value="LEGAL_EQUIV">Legal Equivalence</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* MFA Section */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label className="font-medium">Multi-Factor Authentication (MFA)</Label>
                        <p className="text-xs text-muted-foreground">Require MFA for all users in this organization</p>
                      </div>
                    </div>
                    <Switch
                      checked={mfaEnabled}
                      onCheckedChange={async (v) => {
                        setMfaEnabled(v);
                        try {
                          await orgAPI.updateMfaSetting({ mfa_enabled: v });
                          toast.success(`MFA ${v ? 'enabled' : 'disabled'} for this organization`);
                        } catch (e) {
                          setMfaEnabled(!v);
                          toast.error('Failed to update MFA setting');
                        }
                      }}
                      data-testid="org-mfa-toggle"
                    />
                  </div>
                  {mfaEnabled && (
                    <p className="text-xs text-amber-600 mt-2 bg-amber-500/10 rounded p-2">
                      MFA is enforced. Users in this org will be prompted for a TOTP code on login. They must set up MFA via their profile settings (authenticator app).
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                <Settings className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p>No policy found. Seed demo data first.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
